import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { apiService } from '../../../services/api';
import { UserOut, EventOut, EventVenueOut } from '../../../types/api';
import { generateSecurePassword } from '../../../utils/passwordUtils';
import { canAccessAllEvents } from '../../../utils/roleUtils';
import Card from '../../atoms/Card';
import Button from '../../atoms/Button';
import Spacer from '../../atoms/Spacer';
import { FormField } from '../../molecules/FormField';
import { SearchableSelect } from '../../molecules/SearchableSelect';
import './TeamManagement.scss';

interface TeamManagementProps {
  eventId?: number;
}

interface AddTeamMemberForm {
  email: string;
  username: string;
  firstName: string;
  lastName: string;
  role: 'barman' | 'warehouse';
  dateOfBirth: string;
}

const TeamManagement: React.FC<TeamManagementProps> = ({ eventId: initialEventId }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [events, setEvents] = useState<EventOut[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<number | null>(initialEventId || null);
  const [teamMembers, setTeamMembers] = useState<UserOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [venues, setVenues] = useState<EventVenueOut[]>([]);
  const [updatingVenueId, setUpdatingVenueId] = useState<number | null>(null);

  const [formData, setFormData] = useState<AddTeamMemberForm>({
    email: '',
    username: '',
    firstName: '',
    lastName: '',
    role: 'barman',
    dateOfBirth: ''
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [movingMemberId, setMovingMemberId] = useState<number | null>(null);
  const [isMoving, setIsMoving] = useState(false);
  const [resendingPasswordId, setResendingPasswordId] = useState<number | null>(null);

  // Fetch events for the organizer or all events for super admin
  const fetchEvents = useCallback(async () => {
    try {
      const eventsData = canAccessAllEvents(user)
        ? await apiService.getEvents()
        : await apiService.getMyManagedEvents();
      setEvents(eventsData);
      if (eventsData.length > 0 && !selectedEventId) {
        setSelectedEventId(eventsData[0].id);
      }
    } catch (err: any) {
      setError(err.message || t('errors.general'));
    }
  }, [selectedEventId, t, user]);

  // Fetch team members for the selected event
  const fetchTeamMembers = useCallback(async () => {
    if (!selectedEventId) return;
    
    try {
      setLoading(true);
      const members = await apiService.getUsersByEvent(selectedEventId);
      setTeamMembers(members);
    } catch (err: any) {
      setError(err.message || t('errors.general'));
    } finally {
      setLoading(false);
    }
  }, [selectedEventId, t]);

  const fetchVenues = useCallback(async () => {
    if (!selectedEventId) {
      setVenues([]);
      return;
    }
    try {
      const venuesData = await apiService.getEventVenues(selectedEventId);
      setVenues(venuesData);
    } catch (err: any) {
      console.error('Failed to fetch venues:', err);
    }
  }, [selectedEventId]);

  // Fetch events on component mount
  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Fetch team members when event selection changes
  useEffect(() => {
    if (selectedEventId) {
      fetchTeamMembers();
      fetchVenues();
    }
  }, [fetchTeamMembers, fetchVenues, selectedEventId]);

  const handleInputChange = (field: keyof AddTeamMemberForm, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSetBarmanVenue = async (memberId: number, venueId: number | null) => {
    try {
      setUpdatingVenueId(memberId);
      await apiService.setBarmanVenue(memberId, venueId);
      await fetchTeamMembers();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || t('errors.general'));
    } finally {
      setUpdatingVenueId(null);
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.email) {
      errors.email = t('errors.validation.required');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = t('errors.validation.email');
    }

    if (!formData.username) {
      errors.username = t('errors.validation.required');
    }

    if (!formData.firstName) {
      errors.firstName = t('errors.validation.required');
    }

    if (!formData.lastName) {
      errors.lastName = t('errors.validation.required');
    }

    if (!formData.dateOfBirth) {
      errors.dateOfBirth = t('errors.validation.required');
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    if (!selectedEventId) {
      setError(t('teamManagement.noEventSelected'));
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      
      // Generate a secure password
      const securePassword = generateSecurePassword(12);
      
      // Create the barman user
      // Backend handles sending the welcome email with credentials
      const newUser = await apiService.register({
        email: formData.email,
        username: formData.username,
        first_name: formData.firstName,
        last_name: formData.lastName,
        role: formData.role,
        event_id: selectedEventId,
        date_of_birth: formData.dateOfBirth,
        password: securePassword
      });

      // Reset form
      setFormData({
        email: '',
        username: '',
        firstName: '',
        lastName: '',
        role: 'barman',
        dateOfBirth: ''
      });
      setShowAddForm(false);
      
      // Refresh team members
      await fetchTeamMembers();
      
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || t('errors.general'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveMember = async (_memberId: number) => {
    try {
      // TODO: Replace with actual API call when backend supports it
      // await apiService.removeTeamMember(memberId);
      
      // Refresh team members
      await fetchTeamMembers();
    } catch (err: any) {
      setError(err.message || t('errors.general'));
    }
  };

  const handleMoveMember = async (memberId: number, targetEventId: number) => {
    if (!targetEventId) return;

    try {
      setIsMoving(true);
      await apiService.updateUser(memberId, {
        event_id: targetEventId
      });
      
      // Refresh team members
      await fetchTeamMembers();
    } catch (err: any) {
      setError(err.message || t('errors.general'));
    } finally {
      setIsMoving(false);
    }
  };

  const handleResendPassword = async (member: UserOut) => {
    try {
      setResendingPasswordId(member.id);
      setError(null);
      
      // Generate a new secure password
      const securePassword = generateSecurePassword(12);
      
      // Try to reset password using dedicated endpoint first
      let passwordUpdated = false;
      let lastError: any = null;
      
      try {
        await apiService.resetUserPassword(member.id, securePassword);
        passwordUpdated = true;
      } catch (resetError: any) {
        lastError = resetError;
        console.log('Reset password endpoint failed, trying updateUser:', resetError.response?.status, resetError.response?.data);
        
        // If reset endpoint doesn't exist, try updating via updateUser
        // Some backends accept password in the update call
        try {
          await apiService.updateUser(member.id, {
            password: securePassword
          } as any);
          passwordUpdated = true;
        } catch (updateError: any) {
          lastError = updateError;
          console.error('UpdateUser with password also failed:', updateError.response?.status, updateError.response?.data);
          
          // If both fail, throw error - don't send email with wrong password
          const errorMessage = updateError.response?.data?.detail || 
            resetError.response?.data?.detail || 
            updateError.message ||
            t('teamManagement.passwordResetFailed');
          throw new Error(errorMessage);
        }
      }
      
      // Backend handles sending the email, so we don't send it from frontend
      if (passwordUpdated) {
        // Clear any previous errors on success
        setError(null);
      }
      
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || t('teamManagement.resendPasswordEmailError'));
    } finally {
      setResendingPasswordId(null);
    }
  };

  const eventOptions = events.map((event) => ({ value: event.id, label: event.name }));

  if (loading) {
    return (
      <Card variant="elevated" className="team-management">
        <div className="team-management__loading">
          <p>{t('common.loading')}</p>
        </div>
      </Card>
    );
  }

  if (!selectedEventId) {
    return (
      <Card variant="elevated" className="team-management">
        <div className="team-management__event-selector">
          <h3 className="team-management__title">{t('teamManagement.title')}</h3>
          <div className="team-management__event-selector-content">
            <SearchableSelect<number>
              name="eventSelect"
              label={t('teamManagement.selectEvent')}
              placeholder={t('teamManagement.selectEventPlaceholder')}
              options={eventOptions}
              value={selectedEventId}
              onChange={(eventId) => {
                if (eventId !== null) setSelectedEventId(eventId);
              }}
              disabled={events.length === 0}
            />
          </div>
          {events.length === 0 && (
            <p className="team-management__no-events">{t('teamManagement.noEvents')}</p>
          )}
        </div>
      </Card>
    );
  }

  return (
    <>
    <Card variant="elevated" className="team-management-event-selector">
      {/* Event Selector */}
      <div className="team-management__event-selector">
        <h3 className="team-management__title">{t('teamManagement.title')}</h3>
        <div className="team-management__event-selector-content">
          <SearchableSelect<number>
            name="eventSelect"
            label={t('teamManagement.selectEvent')}
            placeholder={t('teamManagement.selectEventPlaceholder')}
            options={eventOptions}
            value={selectedEventId}
            onChange={(eventId) => {
              if (eventId !== null) setSelectedEventId(eventId);
            }}
            disabled={events.length === 0}
          />
        </div>
      </div>
      </Card>
    <Spacer size="md" />
    <Card variant="elevated" className="team-management-add-bartender">
      {/* Add Bartender Button */}
      {selectedEventId && (
        <>
          <div className="team-management__header">
            <h4 className="team-management__subtitle">
              {t('teamManagement.managingEvent')}: {events.find(e => e.id === selectedEventId)?.name}
            </h4>
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowAddForm(!showAddForm)}
            >
              {showAddForm ? t('common.cancel') : t('teamManagement.addMember')}
            </Button>
          </div>
        </>
      )}

      {error && (
        <div className="team-management__error">
          {error}
        </div>
      )}
      {/* Add Bartender Form */}
      {showAddForm && (
        <form onSubmit={handleSubmit} className="team-management__form">
          {/* Event Assignment Information */}
          <div className="team-management__event-assignment">
            <h4 className="team-management__event-assignment-title">
              {t('teamManagement.assigningToEvent')}
            </h4>
            <div className="team-management__event-assignment-info">
              <div className="team-management__event-assignment-item">
                <label className="team-management__event-assignment-label">
                  {t('teamManagement.eventName')}:
                </label>
                <span className="team-management__event-assignment-value">
                  {events.find(e => e.id === selectedEventId)?.name}
                </span>
              </div>
              <div className="team-management__event-assignment-item">
                <label className="team-management__event-assignment-label">
                  {t('teamManagement.eventDate')}:
                </label>
                <span className="team-management__event-assignment-value">
                  {events.find(e => e.id === selectedEventId)?.start_date ? 
                    new Date(events.find(e => e.id === selectedEventId)!.start_date!).toLocaleDateString() : 
                    t('teamManagement.dateNotSet')
                  }
                </span>
              </div>
              <div className="team-management__event-assignment-item">
                <label className="team-management__event-assignment-label">
                  {t('teamManagement.eventLocation')}:
                </label>
                <span className="team-management__event-assignment-value">
                  {events.find(e => e.id === selectedEventId)?.address || t('teamManagement.locationNotSet')}
                </span>
              </div>
            </div>
          </div>

          <div className="team-management__form-fields">
            <FormField
              type="select"
              name="role"
              label={t('teamManagement.role')}
              value={formData.role}
              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => handleInputChange('role', e.target.value as 'barman' | 'warehouse')}
              disabled={isSubmitting}
            >
              <option value="barman">{t('roles.bartender')}</option>
              <option value="warehouse">{t('roles.warehouse')}</option>
            </FormField>

            <FormField
              type="email"
              name="email"
              label={t('auth.register.email')}
              error={formErrors.email}
              required
              value={formData.email}
              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => handleInputChange('email', e.target.value)}
              placeholder={t('auth.register.emailPlaceholder')}
              disabled={isSubmitting}
            />

            <FormField
              type="text"
              name="username"
              label={t('auth.register.username')}
              error={formErrors.username}
              required
              value={formData.username}
              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => handleInputChange('username', e.target.value)}
              placeholder={t('auth.register.username')}
              disabled={isSubmitting}
            />

            <div className="team-management__name-fields">
              <FormField
                type="text"
                name="firstName"
                label={t('auth.register.firstName')}
                error={formErrors.firstName}
                required
                value={formData.firstName}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => handleInputChange('firstName', e.target.value)}
                placeholder={t('auth.register.firstName')}
                disabled={isSubmitting}
              />

              <FormField
                type="text"
                name="lastName"
                label={t('auth.register.lastName')}
                error={formErrors.lastName}
                required
                value={formData.lastName}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => handleInputChange('lastName', e.target.value)}
                placeholder={t('auth.register.lastName')}
                disabled={isSubmitting}
              />
            </div>

            <FormField
              type="date"
              name="dateOfBirth"
              label={t('auth.register.dateOfBirth')}
              error={formErrors.dateOfBirth}
              required
              value={formData.dateOfBirth}
              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => handleInputChange('dateOfBirth', e.target.value)}
              disabled={isSubmitting}
            />
          </div>

          <div className="team-management__form-actions">
            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? t('common.loading') : t('teamManagement.addMember')}
            </Button>
          </div>
        </form>
      )}
    </Card>
    <Spacer size="md" />
    <Card variant="elevated" className="team-management-members">
      <div className="team-management__members">
        <h4 className="team-management__members-title">{t('teamManagement.members')}</h4>
        
        {teamMembers.length === 0 ? (
          <p className="team-management__empty">{t('teamManagement.noMembers')}</p>
        ) : (
          <div className="team-management__members-list">
            {teamMembers.map((member) => (
              <div key={member.id} className="team-management__member">
                <div className="team-management__member-info">
                  <span className="team-management__member-name">
                    {member.first_name} {member.last_name}
                  </span>
                  <span className="team-management__member-role">
                    {t(member.role === 'barman' ? 'roles.bartender' : `roles.${member.role}`)}
                  </span>
                  <span className="team-management__member-email">
                    {member.email}
                  </span>
                </div>
                <div className="team-management__member-actions">
                  <div className="team-management__move-section">
                    <SearchableSelect<number>
                      name="event_select"
                      placeholder={t('teamManagement.moveMember')}
                      // Action picker: always shows the placeholder again after a pick.
                      value={null}
                      options={eventOptions.filter((option) => option.value !== selectedEventId)}
                      onChange={(targetEventId) => {
                        if (targetEventId) {
                          handleMoveMember(member.id, targetEventId);
                        }
                      }}
                      disabled={isMoving}
                      className="team-management__event-select"
                    />
                  </div>
                  {member.role === 'barman' && (
                    <div className="team-management__venue-section">
                      <SearchableSelect<number>
                        name="venue_select"
                        placeholder={t('teamManagement.noVenueAssigned', { defaultValue: '— Sin barra —' })}
                        clearable
                        options={venues.map((venue) => ({
                          value: venue.id,
                          label: `${venue.name}${venue.venue_type_name ? ` (${venue.venue_type_name})` : ''}`,
                        }))}
                        value={member.event_venue_id ?? null}
                        onChange={(venueId) => handleSetBarmanVenue(member.id, venueId)}
                        disabled={updatingVenueId === member.id}
                        className="team-management__venue-select"
                      />
                    </div>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleResendPassword(member)}
                    disabled={resendingPasswordId === member.id}
                    className="team-management__resend-password-btn"
                  >
                    {resendingPasswordId === member.id ? t('common.sending') : t('teamManagement.resendPassword')}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveMember(member.id)}
                    className="team-management__remove-btn"
                  >
                    {t('common.remove')}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>

    </>
  );
};

export default TeamManagement; 